// Period window of the analysis screens (Phase 4C-3, behind FEATURES.REPORT_ANALYSIS_WINDOW). Ranges are inclusive "YYYY-MM" months, the unit the
// backend windows on (utils/reporting/reportQuery.js: default 12 months, at most 24). Everything here is pure so the screens and the tests share it.
export const DEFAULT_WINDOW_MONTHS = 12;
export const MAX_WINDOW_MONTHS = 24;

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const pad = (value) => String(value).padStart(2, "0");

export const isMonthKey = (value) => MONTH_PATTERN.test(String(value ?? ""));
const toIndex = (key) => {
  const [, year, month] = MONTH_PATTERN.exec(key);
  return Number(year) * 12 + Number(month) - 1;
};
const fromIndex = (index) => `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;

export const currentMonthKey = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
export const addMonths = (key, count) => fromIndex(toIndex(key) + count);
// Number of months from `from` to `to`, both included.
export const monthSpan = (from, to) => toIndex(to) - toIndex(from) + 1;

export const defaultRange = (now = new Date()) => {
  const to = currentMonthKey(now);
  return { from: addMonths(to, -(DEFAULT_WINDOW_MONTHS - 1)), to };
};

export const maxRange = (now = new Date()) => {
  const to = currentMonthKey(now);
  return { from: addMonths(to, -(MAX_WINDOW_MONTHS - 1)), to };
};

// { ok: true } or { ok: false, code } with code in format | order | future | tooLong. Nothing is clamped silently: the user is told.
export function validateRange(range, now = new Date()) {
  const { from, to } = range || {};
  if (!isMonthKey(from) || !isMonthKey(to)) return { ok: false, code: "format" };
  if (toIndex(from) > toIndex(to)) return { ok: false, code: "order" };
  if (toIndex(to) > toIndex(currentMonthKey(now))) return { ok: false, code: "future" };
  if (monthSpan(from, to) > MAX_WINDOW_MONTHS) return { ok: false, code: "tooLong" };
  return { ok: true };
}

export const canLoadEarlier = (range) => monthSpan(range.from, range.to) < MAX_WINDOW_MONTHS;

// "Load earlier": 12 more months before `from`, never past the 24-month cap.
export const loadEarlier = (range) => {
  const room = MAX_WINDOW_MONTHS - monthSpan(range.from, range.to);
  return room > 0 ? { ...range, from: addMonths(range.from, -Math.min(DEFAULT_WINDOW_MONTHS, room)) } : range;
};

// First instant of `from` and last instant of `to` (local time), for filtering client-side lists by a date.
export const rangeBounds = (range) => {
  const [fromYear, fromMonth] = range.from.split("-").map(Number);
  const [toYear, toMonth] = range.to.split("-").map(Number);
  return { start: new Date(fromYear, fromMonth - 1, 1), end: new Date(toYear, toMonth, 1) }; // end exclusive
};

export const isDateInRange = (value, range) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const { start, end } = rangeBounds(range);
  return date >= start && date < end;
};

// The answer of the window routes carries `window` ({ from, to, clamped, defaulted }); a backend without them answers the legacy body.
export const isWindowAnswer = (data) => Boolean(data && Array.isArray(data.data) && data.window && typeof data.window.from === "string");
